import { useMemo, useState } from 'react';
import {
  Building2,
  CircleDollarSign,
  MapPin,
  Truck,
  Users,
} from 'lucide-react';
import {
  TRANSPORT_VENDORS,
  VENDOR_CONTRACT_STATUS,
} from './mockTransportVendorData';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  border: '#CBD5E1',
  blue: '#3B82F6',
  blueLight: '#EFF6FF',
  text: '#1E293B',
  muted: '#64748B',
};


const lightCard = {
  backgroundColor: P.white,
  borderColor: P.grid,
};

function formatAed(value) {
  return `${Number(value || 0).toLocaleString('en-AE')} AED`;
}

function MetricBadge({ icon: Icon, label, value, sublabel }) {
  return (
    <div
      className="flex items-center gap-4 rounded-2xl border p-5 shadow-sm"
      style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
    >
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full shadow-sm"
        style={{
          backgroundColor: P.blueLight,
          boxShadow: `inset 0 0 0 2px ${P.white}, 0 0 0 1px ${P.grid}`,
        }}
      >
        <Icon className="h-6 w-6" style={{ color: P.blue }} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p
          className="text-[10px] font-bold uppercase tracking-widest"
          style={{ color: P.muted }}
        >
          {label}
        </p>
        <p className="font-mono text-2xl font-black tabular-nums" style={{ color: P.text }}>
          {value}
        </p>
        {sublabel && (
          <p className="mt-0.5 text-[11px] font-medium" style={{ color: P.muted }}>
            {sublabel}
          </p>
        )}
      </div>
    </div>
  );
}

function ContractStatusBadge({ status }) {
  const meta = VENDOR_CONTRACT_STATUS[status] || VENDOR_CONTRACT_STATUS.Active;
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
      style={{
        backgroundColor: meta.bg,
        color: meta.color,
        boxShadow: `inset 0 0 0 1px ${meta.ring}`,
      }}
    >
      {meta.label}
    </span>
  );
}

function FleetStatusPill({ status }) {
  const isActive = /transit|loading|dispatched/i.test(status);
  const isWarn = /review|standby|maintenance/i.test(status);
  const color = /grounded|expired/i.test(status)
    ? '#DC2626'
    : isWarn
      ? '#D97706'
      : isActive
        ? '#059669'
        : P.muted;
  const bg = /grounded|expired/i.test(status)
    ? '#FEF2F2'
    : isWarn
      ? '#FFFBEB'
      : isActive
        ? '#ECFDF5'
        : P.silver;

  return (
    <span
      className="inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase"
      style={{ backgroundColor: bg, color }}
    >
      {status}
    </span>
  );
}

function FleetDeploymentPanel({ vendor }) {
  if (!vendor) return null;

  const deployed = vendor.fleetDeployment.filter((t) => !/idle|grounded|—/i.test(t.status)).length;

  return (
    <section
      className="mt-6 overflow-hidden rounded-2xl border shadow-sm"
      style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      aria-label={`Fleet deployment for ${vendor.companyName}`}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4"
        style={{
          backgroundColor: P.silver,
          borderColor: P.grid,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
        }}
      >
        <div>
          <p
            className="text-[10px] font-bold uppercase tracking-widest"
            style={{ color: P.blue }}
          >
            Live Fleet Deployment
          </p>
          <h3 className="text-base font-extrabold" style={{ color: P.text }}>
            {vendor.companyName}{' '}
            <span className="font-mono text-sm font-bold" style={{ color: P.muted }}>
              ({vendor.vendorId})
            </span>
          </h3>
        </div>
        <div className="flex gap-4 text-xs font-semibold" style={{ color: P.muted }}>
          <span>
            Active tankers:{' '}
            <strong style={{ color: P.text }}>{vendor.totalFleets}</strong>
          </span>
          <span>
            On dispatch:{' '}
            <strong style={{ color: P.blue }}>{deployed}</strong>
          </span>
          <span>
            Pending invoices:{' '}
            <strong style={{ color: P.text }}>{formatAed(vendor.pendingInvoices)}</strong>
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: P.silver, color: P.muted }}
            >
              <th className="px-5 py-3">Tanker ID</th>
              <th className="px-5 py-3">Plate</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Route / Location</th>
              <th className="px-5 py-3">Cargo</th>
              <th className="px-5 py-3">Driver</th>
              <th className="px-5 py-3">ETA</th>
            </tr>
          </thead>
          <tbody>
            {vendor.fleetDeployment.map((tanker) => (
              <tr
                key={tanker.tankerId}
                style={{ borderTop: `1px solid ${P.grid}` }}
              >
                <td className="px-5 py-3.5 font-mono text-xs font-bold" style={{ color: P.blue }}>
                  {tanker.tankerId}
                </td>
                <td className="px-5 py-3.5 font-mono font-semibold" style={{ color: P.text }}>
                  {tanker.plate}
                </td>
                <td className="px-5 py-3.5">
                  <FleetStatusPill status={tanker.status} />
                </td>
                <td className="px-5 py-3.5">
                  <span className="flex items-center gap-1.5 font-medium" style={{ color: P.text }}>
                    <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: P.muted }} />
                    {tanker.route}
                  </span>
                </td>
                <td className="px-5 py-3.5 font-medium" style={{ color: P.muted }}>
                  {tanker.cargo}
                </td>
                <td className="px-5 py-3.5 font-medium" style={{ color: P.text }}>
                  {tanker.driver}
                </td>
                <td className="px-5 py-3.5 font-mono text-xs" style={{ color: P.muted }}>
                  {tanker.eta}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function TransportVendorMaster() {
  const [selectedVendorId, setSelectedVendorId] = useState(null);

  const summary = useMemo(() => {
    const activeVendors = TRANSPORT_VENDORS.filter((v) => v.contractStatus === 'Active').length;
    const dispatchedTankers = TRANSPORT_VENDORS.reduce((sum, v) => {
      if (v.contractStatus !== 'Active') return sum;
      return sum + v.totalFleets;
    }, 0);
    const pendingPayouts = TRANSPORT_VENDORS.reduce((sum, v) => sum + v.pendingInvoices, 0);
    return { activeVendors, dispatchedTankers, pendingPayouts };
  }, []);

  const selectedVendor = useMemo(
    () => TRANSPORT_VENDORS.find((v) => v.vendorId === selectedVendorId) || null,
    [selectedVendorId],
  );

  const handleSelectVendor = (vendorId) => {
    setSelectedVendorId(vendorId);
  };

  const handleViewFleet = (e, vendorId) => {
    e.stopPropagation();
    setSelectedVendorId(vendorId);
  };

  return (
    <div className="w-full font-sans">
      <header className="mb-8">
        <span
          className="inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{
            backgroundColor: P.white,
            borderColor: P.grid,
            borderWidth: 1,
            borderStyle: 'solid',
            color: P.blue,
          }}
        >
          OGTS LogiCore™ · Outsourced Fleet Network
        </span>
        <h1
          className="mt-2 text-2xl font-black tracking-tight sm:text-3xl"
          style={{ color: P.text }}
        >
          Transport Vendor Master
        </h1>
        <p
          className="mt-1 text-xs font-bold uppercase tracking-wider"
          style={{ color: P.muted }}
        >
          Third-party fuel transport · Tanker contracts · Invoice clearance
        </p>
      </header>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricBadge
          icon={Users}
          label="Total Active Vendors"
          value={summary.activeVendors}
          sublabel={`of ${TRANSPORT_VENDORS.length} registered partners`}
        />
        <MetricBadge
          icon={Truck}
          label="Total Dispatched Tankers"
          value={summary.dispatchedTankers}
          sublabel="Active contract fleet count"
        />
        <MetricBadge
          icon={CircleDollarSign}
          label="Total Pending Payouts"
          value={formatAed(summary.pendingPayouts)}
          sublabel="Awaiting finance approval"
        />
      </div>

      <section
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div
          className="flex items-center gap-2 border-b px-5 py-4"
          style={{
            backgroundColor: P.silver,
            borderColor: P.grid,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          <Building2 className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
              Vendor Registry
            </h2>
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: P.muted }}>
              Click a row to inspect live fleet deployment
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead>
              <tr
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: P.white, color: P.muted }}
              >
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Vendor ID
                </th>
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Company Name
                </th>
                <th className="border-b px-5 py-3 text-center" style={{ borderColor: P.grid }}>
                  Active Tankers
                </th>
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Contract Status
                </th>
                <th className="border-b px-5 py-3 text-right" style={{ borderColor: P.grid }}>
                  Pending Invoices
                </th>
                <th className="border-b px-5 py-3 text-center" style={{ borderColor: P.grid }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {TRANSPORT_VENDORS.map((vendor) => {
                const isSelected = selectedVendorId === vendor.vendorId;
                return (
                  <tr
                    key={vendor.vendorId}
                    onClick={() => handleSelectVendor(vendor.vendorId)}
                    className="cursor-pointer transition"
                    style={{
                      backgroundColor: isSelected ? P.blueLight : P.white,
                      borderTop: `1px solid ${P.grid}`,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = P.silver;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isSelected ? P.blueLight : P.white;
                    }}
                  >
                    <td className="px-5 py-4 font-mono text-xs font-bold" style={{ color: P.blue }}>
                      {vendor.vendorId}
                    </td>
                    <td className="px-5 py-4 font-semibold" style={{ color: P.text }}>
                      {vendor.companyName}
                    </td>
                    <td
                      className="px-5 py-4 text-center font-mono text-base font-black tabular-nums"
                      style={{ color: P.text }}
                    >
                      {vendor.totalFleets}
                    </td>
                    <td className="px-5 py-4">
                      <ContractStatusBadge status={vendor.contractStatus} />
                    </td>
                    <td
                      className="px-5 py-4 text-right font-mono font-bold tabular-nums"
                      style={{ color: P.text }}
                    >
                      {formatAed(vendor.pendingInvoices)}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={(e) => handleViewFleet(e, vendor.vendorId)}
                        className="rounded-lg px-3 py-1.5 text-xs font-bold text-white shadow-sm transition"
                        style={{ backgroundColor: P.blue }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2563EB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = P.blue;
                        }}
                      >
                        View Fleet
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {selectedVendor && <FleetDeploymentPanel vendor={selectedVendor} />}
    </div>
  );
}
